import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useApp } from "../context/AppContext";
import api from "../api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Mail, Plus, Pencil, Trash2, Send, Paperclip, FileText } from "lucide-react";

const CATEGORIES = ["fattura", "sollecito", "sollecito_2", "sollecito_3", "contratto", "antiriciclaggio", "commercialista", "altro"];

export default function Emails() {
  const { t, entity } = useApp();
  const [params] = useSearchParams();
  const invoiceId = params.get("invoice") || "";
  const [smtp, setSmtp] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [log, setLog] = useState([]);
  const [vaultDocs, setVaultDocs] = useState([]);
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [tplId, setTplId] = useState("");
  const [attachIds, setAttachIds] = useState([]);
  const [sending, setSending] = useState(false);
  const [tplOpen, setTplOpen] = useState(false);
  const [tplForm, setTplForm] = useState({ id: "", name: "", category: "fattura", subject: "", body: "" });

  const load = useCallback(() => {
    api.get("/email/status").then((r) => setSmtp(r.data)).catch(() => {});
    api.get("/email/templates").then((r) => setTemplates(r.data)).catch(() => {});
  }, []);
  const loadEntity = useCallback(() => {
    api.get(`/email/log?entity_id=${entity}`).then((r) => setLog(r.data)).catch(() => {});
    api.get(`/vault?entity_id=${entity}`).then((r) => setVaultDocs(r.data)).catch(() => {});
  }, [entity]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadEntity(); }, [loadEntity]);

  const applyTemplate = (tpl) => {
    setTplId(tpl.id);
    setSubject(tpl.subject);
    setBody(tpl.body);
  };

  useEffect(() => {
    if (invoiceId && templates.length && !tplId) {
      const tpl = templates.find((x) => x.category === "fattura");
      if (tpl) applyTemplate(tpl);
    }
  }, [invoiceId, templates]); // eslint-disable-line react-hooks/exhaustive-deps

  const send = async () => {
    setSending(true);
    try {
      await api.post("/email/send", {
        entity_id: entity, to, subject, body,
        template_id: tplId || null, invoice_id: invoiceId || null, attachment_ids: attachIds,
      });
      toast.success(t("email.sentOk"));
      setTo(""); setSubject(""); setBody(""); setTplId(""); setAttachIds([]);
      loadEntity();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : t("msg.error"));
    } finally { setSending(false); }
  };

  const openTpl = (tpl) => {
    setTplForm(tpl ? { id: tpl.id, name: tpl.name, category: tpl.category, subject: tpl.subject, body: tpl.body }
                   : { id: "", name: "", category: "fattura", subject: "", body: "" });
    setTplOpen(true);
  };

  const saveTpl = async () => {
    try {
      const payload = { name: tplForm.name, category: tplForm.category, subject: tplForm.subject, body: tplForm.body };
      if (tplForm.id) await api.put(`/email/templates/${tplForm.id}`, payload);
      else await api.post("/email/templates", payload);
      toast.success(t("msg.saved"));
      setTplOpen(false);
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : t("msg.error"));
    }
  };

  const removeTpl = async (id) => {
    try {
      await api.delete(`/email/templates/${id}`);
      toast.success(t("msg.deleted"));
      load();
    } catch {
      toast.error(t("msg.error"));
    }
  };

  const toggleAttach = (id) => {
    setAttachIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  return (
    <div className="space-y-6" data-testid="emails-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-3">
            <Mail className="h-7 w-7 text-primary" />{t("email.title")}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">{t("email.subtitle")}</p>
        </div>
        {smtp && (
          <Badge variant={smtp.configured ? "secondary" : "destructive"} data-testid="smtp-status-badge">
            {smtp.configured ? `SMTP: ${smtp.username}` : t("email.smtpMissing")}
          </Badge>
        )}
      </div>

      <div className="grid gap-6 lg:grid-cols-5">
        <Card className="lg:col-span-3" data-testid="email-compose-card">
          <CardHeader><CardTitle>{t("email.new")}</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>{t("email.to")}</Label>
                <Input type="email" value={to} onChange={(e) => setTo(e.target.value)} placeholder="nome@esempio.it" data-testid="email-to-input" />
              </div>
              <div>
                <Label>{t("email.template")}</Label>
                <Select value={tplId} onValueChange={(v) => { const tpl = templates.find((x) => x.id === v); if (tpl) applyTemplate(tpl); }}>
                  <SelectTrigger data-testid="email-template-select"><SelectValue placeholder={t("email.noTemplate")} /></SelectTrigger>
                  <SelectContent>
                    {templates.map((tpl) => (
                      <SelectItem key={tpl.id} value={tpl.id} data-testid={`tpl-option-${tpl.id}`}>
                        {tpl.name} · {t(`email.cat.${tpl.category}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>{t("email.subject")}</Label>
              <Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="email-subject-input" />
            </div>
            <div>
              <Label>{t("email.body")}</Label>
              <textarea
                className="flex min-h-[180px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                value={body} onChange={(e) => setBody(e.target.value)} data-testid="email-body-input" />
              <p className="text-[11px] text-muted-foreground mt-1">{t("email.placeholdersHint")}</p>
            </div>
            {invoiceId && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="invoice-attach-note">
                <FileText className="h-3.5 w-3.5 text-primary" />{t("email.invoiceAttached")}
              </div>
            )}
            {vaultDocs.length > 0 && (
              <div>
                <Label className="flex items-center gap-1.5"><Paperclip className="h-3.5 w-3.5" />{t("email.attachments")}</Label>
                <div className="mt-1.5 max-h-28 overflow-y-auto rounded-md border border-border p-2 space-y-1">
                  {vaultDocs.map((d) => (
                    <label key={d.id} className="flex items-center gap-2 text-xs cursor-pointer" data-testid={`attach-option-${d.id}`}>
                      <input type="checkbox" checked={attachIds.includes(d.id)} onChange={() => toggleAttach(d.id)} className="accent-primary" />
                      <span className="truncate">{d.original_filename}</span>
                      {d.subject_name && <span className="text-muted-foreground">· {d.subject_name}</span>}
                    </label>
                  ))}
                </div>
              </div>
            )}
            <Button className="w-full" onClick={send} disabled={sending || !to || !subject} data-testid="email-send-button">
              <Send className="h-4 w-4 mr-2" />{sending ? t("common.loading") : t("email.send")}
            </Button>
          </CardContent>
        </Card>

        <Card className="lg:col-span-2" data-testid="email-templates-card">
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle>{t("email.templates")}</CardTitle>
            <Button size="sm" variant="outline" onClick={() => openTpl(null)} data-testid="new-template-button">
              <Plus className="h-4 w-4 mr-1" />{t("email.newTemplate")}
            </Button>
          </CardHeader>
          <CardContent className="space-y-2">
            {templates.map((tpl) => (
              <div key={tpl.id} className="flex items-center justify-between gap-2 rounded-md border border-border px-3 py-2" data-testid={`template-row-${tpl.id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{tpl.name}</div>
                  <Badge variant="secondary" className="text-[10px] mt-0.5">{t(`email.cat.${tpl.category}`)}</Badge>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button size="icon" variant="ghost" data-testid={`edit-template-${tpl.id}`} onClick={() => openTpl(tpl)}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button size="icon" variant="ghost" data-testid={`delete-template-${tpl.id}`} onClick={() => removeTpl(tpl.id)}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <Card data-testid="email-archive-card">
        <CardHeader><CardTitle>{t("email.archive")}</CardTitle></CardHeader>
        <CardContent>
          {log.length === 0 ? (
            <div className="text-sm text-muted-foreground" data-testid="email-archive-empty">{t("email.noEmails")}</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("common.date")}</TableHead>
                  <TableHead>{t("email.to")}</TableHead>
                  <TableHead>{t("email.subject")}</TableHead>
                  <TableHead>{t("email.attachments")}</TableHead>
                  <TableHead>{t("common.status")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {log.map((m) => (
                  <TableRow key={m.id} data-testid={`email-row-${m.id}`}>
                    <TableCell className="font-mono text-xs">{(m.created_at || "").slice(0, 16).replace("T", " ")}</TableCell>
                    <TableCell className="text-sm">{m.to}</TableCell>
                    <TableCell className="text-sm max-w-[280px] truncate">{m.subject}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{(m.attachments || []).length > 0 ? m.attachments.join(", ") : "—"}</TableCell>
                    <TableCell>
                      <Badge variant={m.status === "sent" ? "secondary" : "destructive"} className="text-[10px]" data-testid={`email-status-${m.id}`}>
                        {t(`email.status.${m.status}`)}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={tplOpen} onOpenChange={setTplOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{tplForm.id ? t("email.editTemplate") : t("email.newTemplate")}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>{t("email.templateName")}</Label>
              <Input value={tplForm.name} onChange={(e) => setTplForm({ ...tplForm, name: e.target.value })} data-testid="template-name-input" />
            </div>
            <div>
              <Label>{t("email.category")}</Label>
              <Select value={tplForm.category} onValueChange={(v) => setTplForm({ ...tplForm, category: v })}>
                <SelectTrigger data-testid="template-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c} value={c} data-testid={`tpl-cat-${c}`}>{t(`email.cat.${c}`)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t("email.subject")}</Label>
              <Input value={tplForm.subject} onChange={(e) => setTplForm({ ...tplForm, subject: e.target.value })} data-testid="template-subject-input" />
            </div>
            <div>
              <Label>{t("email.body")}</Label>
              <textarea
                className="flex min-h-[140px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                value={tplForm.body} onChange={(e) => setTplForm({ ...tplForm, body: e.target.value })} data-testid="template-body-input" />
              <p className="text-[11px] text-muted-foreground mt-1">{t("email.placeholdersHint")}</p>
            </div>
            <Button className="w-full" onClick={saveTpl} disabled={!tplForm.name} data-testid="template-save-button">{t("common.save")}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
